import * as Sentry from '@sentry/browser';
import get from 'lodash/get';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createContext } from 'use-context-selector';

import { getUser } from 'src/selectors/userSelectors';
import { Dispatch } from 'src/config/Store';
import {
  ON_MESSAGE_UPDATE,
  ON_NEW_CONVERSATION,
  ON_NEW_MESSAGE,
} from 'src/graphql/chat/subscriptions';
import { Conversation, Message } from 'src/models/Chat';

import { useNetwork } from './useNetwork';

const ChatContext = createContext<any>(null);

export const ChatProvider = ({ chatClient, children }) => {
  const dispatch = useDispatch<Dispatch>();
  const isOnline = useNetwork();

  const auth = useSelector(getUser);

  const onNewMessageSubscription: any = useRef(null);
  const onUpdateMessageSubscription: any = useRef(null);
  const onNewConversationSubscription: any = useRef(null);

  const subscribeToMessages = () => {
    try {
      const observable = chatClient.subscribe({
        query: ON_NEW_MESSAGE,
      });

      const subscription = observable.subscribe((message) => {
        const newMessage: Message = get(message, 'data.onNewMessage');
        dispatch.partners.fetchPartnersIfMissing(newMessage.senderUuid);
        dispatch.chat.addNewMessage(newMessage);

        if (document.hidden) {
          document.title = 'Modern Health Provider Dashboard- You have a new message';
        }
      });
      return subscription;
    } catch (e) {
      Sentry.configureScope((scope) => {
        scope.setTag('chat', 'error_subscribing_to_messages');
        Sentry.captureException(e);
      });
    }
  };

  const subscribeToUpdateMessage = () => {
    try {
      const observable = chatClient.subscribe({
        query: ON_MESSAGE_UPDATE,
      });

      const subscription = observable.subscribe((message) => {
        const nextMessage: Message = get(message, 'data.onMessageUpdate');
        dispatch.chat.updateMessageAndConversation(nextMessage);
      });
      return subscription;
    } catch (e) {
      Sentry.configureScope((scope) => {
        scope.setTag('chat', 'error_subscribing_to_update_messages');
        Sentry.captureException(e);
      });
    }
  };

  const subscribeToConversations = () => {
    try {
      const observable = chatClient.subscribe({
        query: ON_NEW_CONVERSATION,
      });

      const subscription = observable.subscribe((message) => {
        const newConversation: Conversation = get(message, 'data.onNewConversation');
        dispatch.chat.addNewConversation(newConversation);
      });
      return subscription;
    } catch (e) {
      Sentry.configureScope((scope) => {
        scope.setTag('chat', 'error_subscribing_to_conversations');
        Sentry.captureException(e);
      });
    }
  };

  useEffect(() => {
    if (!auth.isAuthenticated && onNewMessageSubscription && onNewMessageSubscription.current) {
      onNewMessageSubscription.current.unsubscribe();
      onNewMessageSubscription.current = null;
    }

    if (!isOnline && onNewMessageSubscription && onNewMessageSubscription.current) {
      onNewMessageSubscription.current.unsubscribe();
      onNewMessageSubscription.current = null;

      return;
    }

    if (!auth.isAuthenticated && onUpdateMessageSubscription?.current) {
      onUpdateMessageSubscription.current.unsubscribe();
      onUpdateMessageSubscription.current = null;
    }

    if (!isOnline && onUpdateMessageSubscription?.current) {
      onUpdateMessageSubscription.current.unsubscribe();
      onUpdateMessageSubscription.current = null;

      return;
    }

    if (!auth.isAuthenticated && onNewConversationSubscription?.current) {
      onNewConversationSubscription.current.unsubscribe();
      onNewConversationSubscription.current = null;
    }

    if (!auth.isAuthenticated) {
      dispatch.chat.reset();
      return;
    }

    if (!isOnline) {
      return;
    }

    if (process.env.NODE_ENV === 'test') {
      return;
    }

    onNewMessageSubscription.current = subscribeToMessages();
    onUpdateMessageSubscription.current = subscribeToUpdateMessage();
    onNewMessageSubscription.current = subscribeToConversations();

    return () => {
      onNewMessageSubscription.current.unsubscribe();
      onUpdateMessageSubscription.current.unsubscribe();
      onNewMessageSubscription.current.unsubscribe();
    };
  }, [auth.isAuthenticated, isOnline]);

  return <ChatContext.Provider value={null}>{children}</ChatContext.Provider>;
};
